import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { forwardRef, useImperativeHandle } from "react";
import { ReactRenderer, useEditor } from "@tiptap/react";
import { type JSONContent } from "@tiptap/core";
import Mention from "@tiptap/extension-mention";
import Placeholder from "@tiptap/extension-placeholder";
import StarterKit from "@tiptap/starter-kit";
import type {
  SuggestionKeyDownProps,
  SuggestionOptions,
  SuggestionProps,
} from "@tiptap/suggestion";
import { Command, CommandItem, CommandList } from "@/components/ui";
import { cn } from "@/lib/utils/cn";
import {
  mentionHighlightChipClassName,
  type MentionOption,
} from "@/lib/utils/mentionUtils";

const MAX_MENTION_SUGGESTIONS = 8;

type MentionSuggestionItem = {
  id: string;
  label: string;
  email?: string;
};

type MentionListHandle = {
  onKeyDown: NonNullable<
    ReturnType<
      NonNullable<SuggestionOptions<MentionSuggestionItem>["render"]>
    >["onKeyDown"]
  >;
};

type MentionListProps = SuggestionProps<MentionSuggestionItem> & {
  isLoading: boolean;
};

const MentionList = forwardRef<MentionListHandle, MentionListProps>(
  (props, ref) => {
    const [selectedIndex, setSelectedIndex] = useState(0);

    const selectItem = (index: number) => {
      if (index >= props.items.length) {
        // Make sure we actually have enough items to select the given index. For
        // instance, if a user presses "Enter" when there are no options, the index will
        // be 0 but there won't be any items, so just ignore the callback here
        return;
      }

      const suggestion = props.items[index];

      const mentionItem: MentionSuggestionItem = {
        id: suggestion.id,
        label: suggestion.label,
        email: suggestion.email,
      };
      props.command(mentionItem);
    };

    const upHandler = () => {
      setSelectedIndex(
        (selectedIndex + props.items.length - 1) % props.items.length,
      );
    };

    const downHandler = () => {
      setSelectedIndex((selectedIndex + 1) % props.items.length);
    };

    const enterHandler = () => {
      selectItem(selectedIndex);
    };

    useEffect(() => {
      setSelectedIndex(0);
    }, [props.items]);

    useImperativeHandle(ref, () => ({
      onKeyDown: ({ event }) => {
        if (event.key === "ArrowUp") {
          upHandler();
          return true;
        }

        if (event.key === "ArrowDown") {
          downHandler();
          return true;
        }

        if (event.key === "Enter") {
          enterHandler();
          return true;
        }

        return false;
      },
    }));

    return props.items.length > 0 ? (
      <div
        className="border-border bg-popover pointer-events-auto z-[99999] max-h-60 min-w-[200px] overflow-y-auto rounded-md border shadow-lg"
        onWheel={(e) => {
          // Ensure wheel events are handled properly
          e.stopPropagation();
        }}
        onScroll={(e) => {
          // Ensure scroll events are handled properly
          e.stopPropagation();
        }}
      >
        <Command>
          <CommandList>
            {props.items.map((item, index) => (
              <CommandItem
                key={item.id}
                value={item.label}
                onSelect={() => {
                  selectItem(index);
                }}
                onClick={() => {
                  selectItem(index);
                }}
                className={cn(
                  "pointer-events-auto cursor-pointer",
                  index === selectedIndex && "bg-accent",
                )}
              >
                {item.label}
              </CommandItem>
            ))}
          </CommandList>
        </Command>
      </div>
    ) : null;
  },
);

MentionList.displayName = "MentionList";

const createMentionSuggestion = ({
  getMentionOptions,
  getIsLoading,
  onOpenChange,
}: {
  getMentionOptions: () => MentionOption[];
  getIsLoading: () => boolean;
  onOpenChange: (isOpen: boolean) => void;
}) => {
  return {
    char: "@",
    allowSpaces: true,
    items: ({ query }: { query: string }) => {
      const mentionOptions = getMentionOptions().filter(
        (option) => option.selectable !== false,
      );
      if (mentionOptions.length === 0) return [];

      const trimmedQuery = query.trim().toLowerCase();
      const filtered =
        trimmedQuery.length === 0
          ? mentionOptions
          : mentionOptions.filter((option) =>
              option.searchValue.includes(trimmedQuery),
            );

      return filtered.slice(0, MAX_MENTION_SUGGESTIONS).map((option) => ({
        id: option.id,
        label: option.display,
        email: option.email,
      }));
    },
    render: () => {
      let component: ReactRenderer<MentionListHandle> | undefined;
      let dropdown: HTMLDivElement | null = null;

      const destroy = () => {
        onOpenChange(false);
        component?.destroy();
        component = undefined;
        if (dropdown) {
          dropdown.remove();
          dropdown = null;
        }
      };

      return {
        onStart: (props: SuggestionProps<MentionSuggestionItem>) => {
          if (typeof document === "undefined") return;

          onOpenChange(true);

          component = new ReactRenderer(MentionList, {
            props: { ...props, isLoading: getIsLoading() },
            editor: props.editor,
          });

          dropdown = document.createElement("div");
          dropdown.style.position = "fixed";
          dropdown.style.zIndex = "99999";
          dropdown.style.pointerEvents = "auto";
          dropdown.style.overflow = "visible";

          // Position the dropdown using the clientRect from TipTap
          if (props.clientRect) {
            const rect = props.clientRect();
            if (rect) {
              dropdown.style.left = `${rect.left}px`;
              dropdown.style.top = `${rect.bottom + 4}px`;
            } else {
              dropdown.style.left = "0";
              dropdown.style.top = "0";
            }
          } else {
            dropdown.style.left = "0";
            dropdown.style.top = "0";
          }

          dropdown.appendChild(component.element);
          document.body.appendChild(dropdown);
        },
        onUpdate: (props: SuggestionProps<MentionSuggestionItem>) => {
          if (!component) return;
          component.updateProps({ ...props, isLoading: getIsLoading() });

          // Update positioning on each update
          if (dropdown && props.clientRect) {
            const rect = props.clientRect();
            if (rect) {
              dropdown.style.left = `${rect.left}px`;
              dropdown.style.top = `${rect.bottom + 4}px`;
            }
          }
        },
        onKeyDown: (props: SuggestionKeyDownProps) => {
          const stopEvent = () => {
            props.event.preventDefault();
            props.event.stopPropagation();
          };

          if (props.event.key === "Escape") {
            destroy();
            stopEvent();
            return true;
          }

          if (component?.ref && "onKeyDown" in component.ref) {
            const handled = component.ref.onKeyDown(props);
            if (handled) {
              stopEvent();
              return true;
            }
          }

          return false;
        },
        onExit: () => {
          onOpenChange(false);
          component?.element.remove();
          component?.destroy();
        },
      };
    },
  };
};

const buildEditorContentFromDisplay = (
  message: string,
  mentionOptions: MentionOption[],
): JSONContent => {
  const content: JSONContent[] = [];
  const addText = (value: string) => {
    value.split("\n").forEach((part, index) => {
      if (index) content.push({ type: "hardBreak" });
      if (part) content.push({ type: "text", text: part });
    });
  };
  let cursor = 0;
  for (const match of message.matchAll(/{{([^{}]+)}}/g)) {
    addText(message.slice(cursor, match.index));
    const id = match[1];
    const option = mentionOptions.find((candidate) => candidate.id === id);
    content.push({
      type: "mention",
      attrs: {
        id,
        label:
          option?.display ??
          (id.startsWith("app:") ? "@Unavailable app (app)" : "@Unknown user"),
        email: option?.email,
      },
    });
    cursor = match.index + match[0].length;
  }
  addText(message.slice(cursor));
  return {
    type: "doc",
    content: [{ type: "paragraph", ...(content.length ? { content } : {}) }],
  };
};

export interface UseTiptapMentionsOptions {
  mentionOptions: MentionOption[];
  isLoading: boolean;
  onUpdate?: (text: string) => void;
  initialContent?: string;
  isEditable?: boolean;
  className?: string;
  placeholder?: string;
  plainText?: boolean;
  enableMentions?: boolean;
  ariaLabel?: string;
}

export const useTiptapMentions = ({
  mentionOptions,
  isLoading,
  onUpdate,
  initialContent,
  isEditable = true,
  className,
  placeholder,
  plainText = false,
  enableMentions = true,
  ariaLabel,
}: UseTiptapMentionsOptions) => {
  const [hasInitializedEditor, setHasInitializedEditor] = useState(false);
  const isSettingEditorContentRef = useRef(false);

  const mentionOptionsRef = useRef<MentionOption[]>(mentionOptions);
  useEffect(() => {
    mentionOptionsRef.current = mentionOptions;
  }, [mentionOptions]);

  const isMentionLoadingRef = useRef(isLoading);
  useEffect(() => {
    isMentionLoadingRef.current = isLoading;
  }, [isLoading]);

  const mentionDropdownOpenRef = useRef(false);

  const mentionSuggestion = useMemo(
    () =>
      createMentionSuggestion({
        getMentionOptions: () => mentionOptionsRef.current,
        getIsLoading: () => isMentionLoadingRef.current,
        onOpenChange: (isOpen) => {
          mentionDropdownOpenRef.current = isOpen;
        },
      }),
    [],
  );

  const editor = useEditor(
    {
      extensions: [
        StarterKit.configure(
          plainText
            ? {
                bold: false,
                italic: false,
                strike: false,
                code: false,
                codeBlock: false,
                heading: false,
                blockquote: false,
                bulletList: false,
                orderedList: false,
                listItem: false,
                horizontalRule: false,
              }
            : {},
        ),
        ...(enableMentions
          ? [
              Mention.configure({
                HTMLAttributes: {
                  class: mentionHighlightChipClassName,
                },
                renderText: ({ node }) =>
                  (typeof node.attrs.label === "string" &&
                  node.attrs.label.length > 0
                    ? node.attrs.label
                    : null) ??
                  (typeof node.attrs.id === "string" && node.attrs.id.length > 0
                    ? node.attrs.id
                    : "user"),
                renderHTML: ({ node }) => [
                  "span",
                  {
                    "data-type": "mention",
                    "data-id": node.attrs.id,
                    class: mentionHighlightChipClassName,
                  },
                  (typeof node.attrs.label === "string" &&
                  node.attrs.label.length > 0
                    ? node.attrs.label
                    : null) ??
                    (typeof node.attrs.id === "string" &&
                    node.attrs.id.length > 0
                      ? node.attrs.id
                      : "user"),
                ],
                suggestion: mentionSuggestion,
              }),
            ]
          : []),
        Placeholder.configure({
          placeholder: placeholder ?? "",
          emptyEditorClass:
            "cursor-text before:content-[attr(data-placeholder)] before:absolute before:top-3 before:left-4 before:text-mauve-11 before:opacity-50 before-pointer-events-none",
        }),
      ],
      editorProps: {
        attributes: {
          class: cn(
            "border-input bg-card ring-offset-background focus-visible:ring-ring w-full rounded-md border px-3 py-2 text-base focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-hidden disabled:cursor-not-allowed disabled:opacity-50 md:text-sm",
            "whitespace-pre-wrap",
            className,
          ),
          ...(placeholder ? { "data-placeholder": placeholder } : {}),
          ...(ariaLabel
            ? {
                role: "textbox",
                "aria-label": ariaLabel,
                "aria-multiline": "true",
              }
            : {}),
        },
      },
      onUpdate: ({ editor: localEditor }) => {
        const nextValue = localEditor.getText({ blockSeparator: "\n" });
        onUpdate?.(nextValue);
      },
    },
    [],
  );

  useEffect(() => {
    if (!editor) return;
    editor.setEditable(isEditable);
  }, [isEditable, editor]);

  // Initialize editor content
  useEffect(() => {
    if (!editor || hasInitializedEditor) return;

    if (initialContent) {
      const displayMessage = initialContent;

      isSettingEditorContentRef.current = true;
      editor.commands.setContent(
        buildEditorContentFromDisplay(displayMessage, mentionOptions),
        false,
        { preserveWhitespace: true },
      );
      isSettingEditorContentRef.current = false;
    } else {
      isSettingEditorContentRef.current = true;
      editor.commands.setContent(
        buildEditorContentFromDisplay("", mentionOptions),
        false,
        { preserveWhitespace: true },
      );
      isSettingEditorContentRef.current = false;
    }
    setHasInitializedEditor(true);
  }, [editor, hasInitializedEditor, initialContent, mentionOptions]);

  useEffect(() => {
    if (!editor || !hasInitializedEditor) return;
    const transaction = editor.state.tr;
    editor.state.doc.descendants((node, position) => {
      if (node.type.name !== "mention") return;
      const option = mentionOptions.find(
        (candidate) => candidate.id === node.attrs.id,
      );
      if (option && option.display !== node.attrs.label) {
        transaction.setNodeMarkup(position, undefined, {
          ...node.attrs,
          label: option.display,
          email: option.email,
        });
      }
    });
    if (transaction.docChanged)
      editor.view.dispatch(transaction.setMeta("addToHistory", false));
  }, [editor, hasInitializedEditor, mentionOptions]);

  const getDisplayMessage = () => {
    if (!editor) return "";
    return editor.getText({ blockSeparator: "\n" });
  };

  const getBackendMessage = () =>
    editor?.getText({
      blockSeparator: "\n",
      textSerializers: {
        mention: ({ node }) => `{{${String(node.attrs.id)}}}`,
      },
    }) ?? "";

  const setContent = useCallback(
    (content: string) => {
      if (!editor) return;
      isSettingEditorContentRef.current = true;
      editor.commands.setContent(
        buildEditorContentFromDisplay(content, mentionOptions),
        false,
        { preserveWhitespace: true },
      );
      isSettingEditorContentRef.current = false;
    },
    [editor, mentionOptions],
  );

  const reset = useCallback(() => {
    if (!editor) return;
    isSettingEditorContentRef.current = true;
    editor.commands.setContent(
      { type: "doc", content: [{ type: "paragraph" }] },
      false,
      { preserveWhitespace: true },
    );
    isSettingEditorContentRef.current = false;
    setHasInitializedEditor(false);
  }, [editor]);

  return {
    editor,
    getDisplayMessage,
    getBackendMessage,
    setContent,
    reset,
    hasInitializedEditor,
    isSettingEditorContentRef,
    mentionDropdownOpenRef,
  };
};
